import React, { useEffect, useState } from 'react';
import pluralize from 'pluralize';

import { APIfetchMemories } from '../api/Memory';

import MemoryFilesGridItemPreview from './MemoryForm/FilesGridItemPreview';
import LoadingSmall from './LoadingSmall';

import { formatDate } from '../helpers';

import type { Memory } from '../types/memory';

interface MemorySelectModalProps {
  timelineId: string;
  show: boolean;
  onConfirm: (memories: Memory[]) => void;
  onDismiss: () => void;
};

const MemorySelectModal: React.FC<MemorySelectModalProps> = ({ timelineId, show, onConfirm, onDismiss }) => {
  const [search, setSearch] = useState('');
  const [selectedMemories, setSelectedMemories] = useState<string[]>([]);
  const [memories, setMemories] = useState<Memory[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchMemories = async () => {
      setLoading(true);
      try {
        const response = await APIfetchMemories({ not_in_timeline_id: timelineId });
        setMemories(response.data);
      } catch (error) {
        console.error('Error fetching memories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMemories();
  }, [timelineId]);

  const switchSelectedMemory = (memoryId: string) => {
    if (selectedMemories.includes(memoryId)) {
      setSelectedMemories(selectedMemories.filter(id => id !== memoryId));
    } else {
      setSelectedMemories([...selectedMemories, memoryId]);
    }
  };

  const filteredMemories = memories.filter(memory => memory.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <wa-dialog label="Select memories" id="dialog-select-memory" className="dialog-select-memory" open={show} style={{ '--width': '600px' }}>
      <div className="memory-select-header">
        <wa-input
          type="text"
          name="search"
          label="Search memories"
          value={search}
          onInput={(e: any) => setSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <LoadingSmall />
      ) : (
        <div className="memory-select-memories wa-stack wa-gap-2">
          {filteredMemories.map((memory) => (
            <wa-card
              key={memory.id}
              className={`memory-card memory-select-memory ${selectedMemories.includes(memory.id) ? 'selected' : ''}`}
              onClick={() => {
                switchSelectedMemory(memory.id);
              }}
            >
              <div className="wa-cluster wa-gap-2 wa-align-items-center">
                <div className="memory-select-memory-checkbox">
                  <wa-checkbox
                    checked={selectedMemories.includes(memory.id)}
                    onChange={() => {
                      switchSelectedMemory(memory.id);
                    }}
                  />
                </div>
                <div className="memory-select-memory-thumbnail memory-card-assets">
                  <div className="memory-card-asset">
                    <MemoryFilesGridItemPreview file={memory.assets[0]} />
                  </div>
                </div>
                <div className="memory-select-memory-info">
                  <h5>{memory.title}</h5>
                  <div className="with-separator">
                    {memory.date && (
                      <span>
                        {formatDate(memory.date)}
                      </span>
                    )}
                    {memory.dedicated_to && (
                      <span>
                        {memory.dedicated_to}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </wa-card>
          ))}
        </div>
      )}
      <wa-button
        slot="footer"
        size="s"
        appearance="outlined"
        onClick={() => onDismiss()}
      >
        Close
      </wa-button>
      <wa-button
        size="s"
        slot="footer"
        variant="brand"
        disabled={loading || selectedMemories.length === 0}
        onClick={() => onConfirm(selectedMemories.map(id => memories.find((memory: Memory) => memory.id === id)).filter(Boolean) as Memory[])}
      >
        {selectedMemories.length > 0 ?
          `Add ${pluralize('selected memory', selectedMemories.length, true)}` :
          'Add selected memories'
        }
      </wa-button>
    </wa-dialog>
  );
}

export default MemorySelectModal;
